import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Wrench, Rocket, ArrowRight, Sparkles, ExternalLink, Calculator, Server } from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MIGRATION_SERVICES } from "@/data/services";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const SCOPE = {
  small: { emoji: "🟢", label: "Small project", color: "#32D74B", note: "Quick fixes — usually a fast turnaround." },
  medium: { emoji: "🟡", label: "Medium project", color: "#FFD60A", note: "Moderate improvements across a few areas." },
  large: { emoji: "🔴", label: "Large project", color: "#FF3B30", note: "Migration or major rebuild — we'll scope it with you." },
};

const GOALS = [
  { value: "migrate", label: "Move my site to Emergent" },
  { value: "manage", label: "Managed hosting & ongoing care" },
  { value: "upgrade", label: "Upgrade my business (full package)" },
  { value: "fix", label: "Just fix the issues on my current host" },
];

const PRIMARY = [
  { key: "scan", emoji: "🔍", title: "Scan My Website", price: "Free", tag: "Instant health report with an overall score and a preview of what's wrong — no card required.", cta: "Scan now", action: "scan" },
  { key: "report", emoji: "📄", title: "AI Report + Build Brief", price: "$29 one-time", tag: "The full report for one website — every issue, the exact step-by-step fixes, an AI build brief, and a downloadable PDF.", cta: "Unlock a report — $29", action: "product", productId: "ai_report" },
  { key: "care", emoji: "📡", title: "Pro", price: "$19.99/mo", highlight: true, badge: "Best value", tag: "Everything, unlimited across all your sites — reports, AI build briefs, PDFs, nightly re-scans, change alerts and the weekly digest.", cta: "Go Pro", action: "checkout", planId: "pro_monthly" },
  { key: "agency", emoji: "🏢", title: "Agency", price: "$49.99/mo", tag: "Everything in Pro across unlimited sites, PLUS agency tools — deep backend scan, programmatic API access & priority support. For people managing many sites.", cta: "Go Agency", action: "checkout", planId: "agency_monthly" },
];

const SECONDARY = [
  { key: "fix", emoji: "🛠️", title: "Need us to fix it?", price: "AI Guided Fix — from $99", tag: "Prefer we implement the fixes for you? Get a transparent, estimate-first quote after your report.", cta: "Get a fix estimate", action: "intake", goal: "fix" },
  { key: "move", emoji: "🚚", title: "Move My Website", price: "Request a quote", tag: "Migration, DNS, email, domain transfer & SSL — moved to Emergent, tested and monitored.", cta: "Get a migration quote", action: "intake", goal: "migrate" },
  { key: "upgrade", emoji: "🚀", title: "Upgrade My Business", price: "Request a quote", tag: "Improvements + migration + business email + Search Console/Analytics + hardening + a savings review.", cta: "Get an upgrade quote", action: "intake", goal: "upgrade" },
];

function ProductDialog({ open, onOpenChange, offer, defaultDomain }) {
  const [domain, setDomain] = useState(defaultDomain || "");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { setDomain(defaultDomain || ""); }, [defaultDomain, open]);

  const go = async () => {
    if (domain.indexOf(".") < 0) { toast.error("Enter a valid website (e.g. example.com)"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/checkout/product", { product_id: offer.productId, domain, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
      else { toast.error("Could not start checkout"); setSubmitting(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed"); setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-white" data-testid="product-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{offer?.name} — {offer?.price}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Which website is this for? You'll go to secure Stripe checkout, then straight to your unlocked report.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <input data-testid="product-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="Website (e.g. example.com)" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#007AFF]/50 focus:outline-none" />
          <button data-testid="product-checkout-submit" onClick={go} disabled={submitting} className="w-full btn-brand text-white rounded-lg px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
            {submitting ? <><Loader2 size={15} className="animate-spin" /> Redirecting…</> : <>Continue to secure checkout — {offer?.price}</>}
          </button>
          <p className="text-[11px] text-zinc-600 text-center">Payments processed by Stripe. One-time charge for this website.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null);
  const [referral, setReferral] = useState("https://app.emergent.sh");
  const [productOffer, setProductOffer] = useState(null);
  const intakeRef = useRef(null);

  const [searchParams] = useSearchParams();
  const scopeKey = searchParams.get("scope");
  const scope = scopeKey && SCOPE[scopeKey] ? SCOPE[scopeKey] : null;

  const [intake, setIntake] = useState({
    name: user?.name || "", email: user?.email || "", domain: searchParams.get("domain") || "", phone: "",
    currentHost: "", registrar: "", dnsProvider: "", emailProvider: "", goal: "migrate", notes: "",
  });
  const [intakeSubmitting, setIntakeSubmitting] = useState(false);
  const [intakeDone, setIntakeDone] = useState(false);
  const [hostInfo, setHostInfo] = useState(null); // { platform, hostLabel, dependencies }
  const [fixEstimate, setFixEstimate] = useState(null);
  const setI = (k, v) => setIntake((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/plans").then((r) => { if (r.data?.emergent_referral_url) setReferral(r.data.emergent_referral_url); }).catch(() => {});
  }, []);

  useEffect(() => {
    const s = scopeKey && SCOPE[scopeKey] ? scopeKey : "medium";
    api.get(`/pricing/fix-estimate?scope=${s}`).then((r) => setFixEstimate(r.data)).catch(() => {});
  }, [scopeKey]);

  useEffect(() => {
    const dom = searchParams.get("domain");
    if (!dom) return;
    api.get(`/scans/${encodeURIComponent(dom)}`).then((r) => {
      const h = r.data?.history?.[0]?.result?.hosting;
      if (h && (h.platform || h.hostLabel || (h.dependencies || []).length)) {
        setHostInfo(h);
        if (h.platform && !intake.currentHost) setI("currentHost", h.platform);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if ((window.location.hash === "#intake" || searchParams.get("domain")) && intakeRef.current) {
      setTimeout(() => intakeRef.current.scrollIntoView({ behavior: "smooth" }), 250);
    }
  }, []); // eslint-disable-line

  const scrollToIntake = () => intakeRef.current?.scrollIntoView({ behavior: "smooth" });

  const checkout = async (key, planId) => {
    setLoadingKey(key);
    try {
      const res = await api.post("/checkout", { plan_id: planId, origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
      else { toast.error("Could not start checkout"); setLoadingKey(null); }
    } catch { toast.error("Checkout failed"); setLoadingKey(null); }
  };

  const handlePath = (p) => {
    if (p.action === "scan") { window.location.href = "/dashboard"; return; }
    if (p.action === "checkout") { checkout(p.key, p.planId); return; }
    if (p.action === "product") { setProductOffer({ productId: p.productId, name: p.title, price: "$29" }); return; }
    if (p.action === "intake") { setI("goal", p.goal); scrollToIntake(); return; }
  };

  const submitIntake = async () => {
    if (intake.email.indexOf("@") < 0 || intake.domain.indexOf(".") < 0) {
      toast.error("Enter a valid email and website."); return;
    }
    setIntakeSubmitting(true);
    try {
      const res = await api.post("/public/migration-intake", { ...intake, scope: scopeKey || "" });
      toast.success(res.data?.message || "Request received!");
      setIntakeDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please try again.");
    } finally { setIntakeSubmitting(false); }
  };

  return (
    <Layout>
      <div className="text-center max-w-2xl mx-auto mb-8">
        <div className="flex items-center justify-center gap-2 text-[#4da3ff] mb-2">
          <Sparkles size={16} /><span className="text-xs font-bold uppercase tracking-[0.2em]">Pick your path</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-white">Scan it, fix it, or let us run it</h1>
        <p className="text-zinc-400 mt-3">Your free scan shows what's wrong. From there, choose the path that fits — do it yourself, have us fix it, move your whole site to Emergent, or upgrade the entire business.</p>
      </div>

      {/* Differentiators */}
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-zinc-300 mb-8" data-testid="differentiators">
        <span className="flex items-center gap-2"><Check size={15} className="text-[#32D74B]" /> We build &amp; move sites for you</span>
        <Link to="/migration" className="flex items-center gap-2 hover:text-white transition-colors"><Calculator size={15} className="text-[#4da3ff]" /> Cut your monthly costs — savings calculator</Link>
        <span className="flex items-center gap-2"><Check size={15} className="text-[#32D74B]" /> Best-value partner recommendations</span>
      </div>

      {/* Emergent hero */}
      <div className="relative overflow-hidden rounded-2xl border border-[#4da3ff]/30 p-7 sm:p-9 mb-10" style={{ background: "linear-gradient(135deg, rgba(0,122,255,0.16), rgba(155,92,255,0.16))" }} data-testid="emergent-hero">
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[#4da3ff]/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[#4da3ff] mb-3">
            <Rocket size={16} /><span className="text-xs font-bold uppercase tracking-[0.2em]">Recommended</span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">Run your whole business from one place — on Emergent</h2>
          <p className="text-zinc-300 text-base mt-3 leading-relaxed">
            Stop juggling hosting, email, forms and a dozen tools that don't talk to each other. We rebuild &amp; move your site to Emergent with a real backend, then add the everyday business tools you use — managed, monitored and secured for you.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <button data-testid="start-intake-btn" onClick={scrollToIntake} className="btn-brand text-white rounded-full px-6 py-3 text-sm font-medium flex items-center gap-2">
              Get my free plan &amp; quote <ArrowRight size={16} />
            </button>
            <a data-testid="build-emergent-link" href={referral} target="_blank" rel="noreferrer" className="text-zinc-300 hover:text-white text-sm inline-flex items-center gap-1.5 transition-colors">
              Explore Emergent <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </div>

      {/* Primary pricing — 3 clean choices */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto mb-14">
        {PRIMARY.map((p) => (
          <div key={p.key} data-testid={`path-${p.key}`} className={`relative bg-[#121212] rounded-2xl p-6 border flex flex-col ${p.highlight ? "border-[#007AFF]/50 shadow-[0_0_40px_-12px_rgba(0,122,255,0.5)]" : "border-white/10"}`}>
            {p.badge && <span className="absolute -top-3 left-6 bg-[#007AFF] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">{p.badge}</span>}
            <div className="flex items-center gap-2">
              <span className="text-xl">{p.emoji}</span>
              <h3 className="font-heading font-semibold text-white text-lg">{p.title}</h3>
            </div>
            <div className="mt-2 font-heading text-2xl font-bold brand-gradient">{p.price}</div>
            {p.key === "report" && (
              <span className="mt-2 inline-flex items-center gap-1.5 self-start rounded-full border border-[#32D74B]/40 bg-[#32D74B]/10 px-2.5 py-1 text-[11px] font-semibold text-[#32D74B]" data-testid="bundle-badge">
                <Check size={12} /> Build Brief included
              </span>
            )}
            <p className="text-zinc-400 text-sm mt-2 flex-1 leading-relaxed">{p.tag}</p>
            <button
              data-testid={`path-cta-${p.key}`}
              onClick={() => handlePath(p)}
              disabled={loadingKey === p.key}
              className={`mt-5 w-full rounded-lg px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${p.key === "scan" ? "bg-white/5 text-white border border-white/10 hover:bg-white/10" : "btn-brand text-white"}`}
            >
              {loadingKey === p.key ? <><Loader2 size={15} className="animate-spin" /> Redirecting…</> : p.cta}
            </button>
          </div>
        ))}
      </div>

      {/* Secondary — after your report */}
      <div className="max-w-5xl mx-auto mb-14">
        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">After your report — want more help?</span>
          <div className="h-px bg-white/10 flex-1" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {SECONDARY.map((p) => (
            <div key={p.key} data-testid={`path-${p.key}`} className="relative bg-[#0f0f0f] rounded-2xl p-5 border border-white/10 flex flex-col">
              <div className="flex items-center gap-2">
                <span className="text-lg">{p.emoji}</span>
                <h3 className="font-heading font-semibold text-white text-base">{p.title}</h3>
              </div>
              <div className="mt-1.5 font-heading text-sm font-bold text-[#4da3ff]">{p.price}</div>
              <p className="text-zinc-400 text-sm mt-2 flex-1 leading-relaxed">{p.tag}</p>
              <button
                data-testid={`path-cta-${p.key}`}
                onClick={() => handlePath(p)}
                className="mt-4 w-full rounded-lg px-4 py-2.5 text-sm font-medium bg-white/5 border border-white/10 hover:bg-white/10 text-white transition-colors flex items-center justify-center gap-2"
              >
                {p.cta}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Intake / quote request */}
      <div ref={intakeRef} id="intake" className="max-w-3xl mx-auto scroll-mt-24" data-testid="migration-intake">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[#4da3ff] mb-2">
            <Rocket size={16} /><span className="text-xs font-bold uppercase tracking-[0.2em]">Request a quote</span>
          </div>
          <h2 className="font-heading text-3xl font-bold text-white tracking-tight">Tell us about your website</h2>
          <p className="text-zinc-400 mt-2 text-sm">No commitment and no fixed price — every site is different. We'll review your setup and reply with a plan and a quote.</p>
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-2xl p-5 mb-5" data-testid="whats-included">
          {intake.goal === "fix" && fixEstimate && (
            <div className="mb-5 pb-5 border-b border-white/10" data-testid="fix-estimate">
              <div className="flex items-center gap-2 mb-3">
                <Wrench size={15} className="text-[#4da3ff]" />
                <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">
                  Estimated done-for-you fix{scope ? ` · ${scope.emoji} ${scope.label}` : ""}
                </h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">Fix work (labor)</span><span className="text-white" data-testid="fix-est-labor">${fixEstimate.labor.toLocaleString()}</span></div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">AI / API processing<span className="text-zinc-600"> · applies on any host</span></span>
                  <span className="text-white" data-testid="fix-est-api">${fixEstimate.apiCost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between"><span className="text-zinc-400">Service &amp; platform markup ({fixEstimate.markupPct}%)</span><span className="text-white" data-testid="fix-est-markup">${fixEstimate.markup.toLocaleString()}</span></div>
                <div className="flex justify-between pt-2 border-t border-white/10 text-base">
                  <span className="text-white font-semibold">Estimated total</span>
                  <span className="brand-gradient font-heading font-extrabold" data-testid="fix-est-total">${fixEstimate.total.toLocaleString()}</span>
                </div>
              </div>
              <p className="text-[11px] text-zinc-600 mt-3">Transparent estimate — final price confirmed after we review your site. API cost reflects real usage; hosting is only charged if you move to Emergent (rolled into your plan).</p>
            </div>
          )}
          {hostInfo && (hostInfo.platform || (hostInfo.dependencies || []).length > 0) && (
            <div className="mb-5 pb-5 border-b border-white/10" data-testid="host-checklist">
              <div className="flex items-center gap-2 mb-2">
                <Server size={15} className="text-[#c9a6ff]" />
                <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">
                  {hostInfo.platform ? `Moving ${intake.domain || "you"} off ${hostInfo.platform}` : `Moving ${intake.domain || "your site"}`}
                </h3>
              </div>
              <p className="text-zinc-400 text-sm mb-3">Based on your scan, here's what we'll carry over so nothing breaks:</p>
              {(hostInfo.dependencies || []).length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="host-checklist-deps">
                  {(hostInfo.dependencies || []).map((d, i) => {
                    const m = ((hostInfo.savings || {}).items || []).find((x) => x.name === d.name)?.monthly;
                    return (
                      <span key={i} data-testid={`host-dep-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-[#0d0d0d] px-3 py-1.5 text-xs text-zinc-200">
                        <Check size={12} className="text-[#32D74B]" /> {d.name}
                        {m > 0 && <span className="text-[#32D74B] font-medium">· ${Math.round(m)}/mo</span>}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-zinc-500 text-xs">No third-party dependencies detected — a clean move.</p>
              )}
              {(hostInfo.savings || {}).totalMonthly > 0 && (
                <p className="text-sm text-white mt-3" data-testid="host-savings-line">
                  💡 Consolidating on Emergent could save <b className="text-[#32D74B]">~${Math.round(hostInfo.savings.totalMonthly)}/mo</b> <span className="text-zinc-400">(~${Math.round(hostInfo.savings.totalAnnual)}/yr, estimated)</span>
                </p>
              )}
            </div>
          )}
          <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide mb-3">What we handle when we move or upgrade you</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            {MIGRATION_SERVICES.map((s) => (
              <div key={s.title} className="flex items-center gap-2 text-sm text-zinc-300">
                <Check size={14} className="text-[#32D74B] shrink-0" /> {s.title}
              </div>
            ))}
          </div>
        </div>

        {scope && !intakeDone && (
          <div className="flex items-center gap-3 rounded-xl border px-4 py-3 mb-5" style={{ borderColor: `${scope.color}55`, background: `${scope.color}12` }} data-testid="scope-banner">
            <span className="text-lg">{scope.emoji}</span>
            <div>
              <p className="text-sm font-semibold text-white">Estimated from your scan: {scope.label}</p>
              <p className="text-zinc-400 text-xs mt-0.5">{scope.note}</p>
            </div>
          </div>
        )}

        {intakeDone ? (
          <div className="bg-[#121212] border border-[#30D158]/30 rounded-2xl p-10 text-center" data-testid="intake-success">
            <Check size={40} className="text-[#30D158] mx-auto mb-3" />
            <h3 className="font-heading text-xl font-semibold text-white">Your request is in</h3>
            <p className="text-zinc-400 text-sm mt-2">We'll reach out to <b className="text-white">{intake.email}</b> shortly with a plan and quote.</p>
          </div>
        ) : (
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-6" data-testid="intake-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Your name" testid="intake-name" value={intake.name} onChange={(v) => setI("name", v)} />
              <Field label="Email" testid="intake-email" value={intake.email} onChange={(v) => setI("email", v)} />
              <Field label="Website" testid="intake-domain" value={intake.domain} onChange={(v) => setI("domain", v)} placeholder="example.com" />
              <Field label="Phone (optional)" testid="intake-phone" value={intake.phone} onChange={(v) => setI("phone", v)} />
              <Field label="Current host" testid="intake-host" value={intake.currentHost} onChange={(v) => setI("currentHost", v)} placeholder="GoDaddy, Wix, Squarespace…" />
              <Field label="Domain registrar" testid="intake-registrar" value={intake.registrar} onChange={(v) => setI("registrar", v)} placeholder="Namecheap, GoDaddy…" />
              <Field label="DNS provider" testid="intake-dns" value={intake.dnsProvider} onChange={(v) => setI("dnsProvider", v)} placeholder="Cloudflare, registrar…" />
              <Field label="Email provider" testid="intake-email-provider" value={intake.emailProvider} onChange={(v) => setI("emailProvider", v)} placeholder="Google Workspace, M365…" />
            </div>
            <div className="mt-4">
              <label className="text-xs text-zinc-400 block mb-1">What do you want to do?</label>
              <select data-testid="intake-goal" value={intake.goal} onChange={(e) => setI("goal", e.target.value)} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#007AFF]/50 focus:outline-none">
                {GOALS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div className="mt-4">
              <label className="text-xs text-zinc-400 block mb-1">Notes (optional)</label>
              <textarea data-testid="intake-notes" value={intake.notes} onChange={(e) => setI("notes", e.target.value)} rows={3} placeholder="Anything else we should know?" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#007AFF]/50 focus:outline-none resize-none" />
            </div>
            <button data-testid="intake-submit" onClick={submitIntake} disabled={intakeSubmitting} className="mt-5 w-full btn-brand text-white rounded-lg px-5 py-3 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {intakeSubmitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Request my quote <ArrowRight size={16} /></>}
            </button>
            <p className="text-[11px] text-zinc-600 mt-3 text-center">We never touch your live site without permission. This just starts a conversation.</p>
          </div>
        )}
      </div>

      <ProductDialog open={!!productOffer} onOpenChange={(v) => !v && setProductOffer(null)} offer={productOffer} defaultDomain={searchParams.get("domain") || ""} />
    </Layout>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 block mb-1">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#007AFF]/50 focus:outline-none"
      />
    </div>
  );
}
